<template>
  <Teleport to="body">
    <div
      v-if="previewState.open"
      class="fixed inset-0 z-[200] flex flex-col bg-black/70"
      @click.self="close"
    >
      <!-- Top bar -->
      <div class="flex items-center gap-3 px-4 py-3 text-white" @click.self="close">
        <span class="flex-1 min-w-0 truncate text-sm font-medium">
          {{ previewState.current?.originalName }}
        </span>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium transition-colors disabled:opacity-50"
          :disabled="downloading"
          @click="download"
        >
          <Download :size="15" />
          {{ downloading ? 'Downloading…' : 'Download' }}
        </button>
        <button
          type="button"
          class="p-2 rounded-lg hover:bg-white/15 transition-colors"
          title="Close (Esc)"
          @click="close"
        >
          <X :size="18" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 min-h-0 flex items-center justify-center p-4 pt-0" @click.self="close">
        <div v-if="previewState.loading" class="text-white/80 text-sm">Loading…</div>

        <div v-else-if="previewState.error" class="text-center text-white/90">
          <p class="text-sm">{{ previewState.error }}</p>
          <button
            type="button"
            class="mt-3 inline-flex items-center px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-sm font-medium"
            @click="download"
          >
            <Download :size="15" class="mr-1.5" /> Download instead
          </button>
        </div>

        <img
          v-else-if="previewState.kind === 'image' && previewState.url"
          :src="previewState.url"
          :alt="previewState.current?.originalName"
          class="max-w-full max-h-full object-contain rounded-lg shadow-2xl bg-white"
          @error="onMediaError"
        />

        <iframe
          v-else-if="previewState.kind === 'pdf' && previewState.url"
          :src="previewState.url"
          :title="previewState.current?.originalName"
          class="w-full h-full max-w-5xl bg-white rounded-lg shadow-2xl"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { Download, X } from 'lucide-vue-next'
import {
  previewState,
  closePreview,
  refreshPreviewUrl,
  downloadAttachment,
} from '@/lib/attachmentPreview'

const downloading = ref(false)
const retriedOnce = ref(false)

function close() {
  closePreview()
}

async function onMediaError() {
  // The stream token may have expired mid-view; re-mint it once (mirror AudioAttachment).
  if (retriedOnce.value) {
    previewState.error = 'Could not load preview'
    return
  }
  retriedOnce.value = true
  await refreshPreviewUrl()
}

async function download() {
  if (!previewState.current) return
  downloading.value = true
  try {
    await downloadAttachment(previewState.current)
  } catch (e) {
    console.error('Download failed:', e)
  } finally {
    downloading.value = false
  }
}

// Reset the retry flag each time a new attachment is opened.
watch(
  () => previewState.current?._id,
  () => {
    retriedOnce.value = false
  },
)

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && previewState.open) close()
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>
